// Copyright (c) 2024-present, Dinocollab Technologies, Inc. and its affiliates. All rights reserved.

// imports
import { Card, styled } from '@mui/material'
import { cloneElement, useEffect, useRef, useState } from 'react'
// types
import type { CSSProperties, FC, ReactElement } from 'react'

export const viewSwitchTransitionClasses = {
  root: 'DinoViewSwitchTransition-root',
  viewWrapper: 'DinoViewSwitchTransition-viewWrapper',
  view: 'DinoViewSwitchTransition-view',
  viewA: 'DinoViewSwitchTransition-viewA',
  viewB: 'DinoViewSwitchTransition-viewB'
}

const classes = viewSwitchTransitionClasses

export const ViewSwitchTransitionStyled = styled('div')(({ theme }) => ({
  [`&.${classes.root}`]: { flex: 1, width: '100%', height: '100%', overflow: 'hidden' },
  // use minHeight: 0 so flex children can shrink and inner scroll areas work
  [`.${classes.viewWrapper}`]: { minHeight: 0, position: 'relative', width: '100%', height: '100%', overflow: 'hidden' },
  [`.${classes.view}`]: { width: '100%', height: '100%' }
}))

export const ViewDefault: FC = () => (
  <Card variant='outlined' sx={{ p: 2 }}>
    Default view content
  </Card>
)

/**
 * Fine-grained control over the animation that plays when switching between Grid and Table view.
 * All timing values are in milliseconds unless noted.
 */
export type TAnimationConfig = {
  /** Duration of the enter (fade-in) animation. Defaults to `220`. */
  enter?: number
  /** Duration of the exit (fade-out) animation. Defaults to `140`. */
  exit?: number
  /** CSS easing function for both enter and exit. Defaults to `'cubic-bezier(0.22, 0.61, 0.36, 1)'`. */
  ease?: string
  /** Delay before the enter animation starts. Defaults to `12`. */
  delayEnter?: number
  /** Delay before the exit animation starts. Defaults to `0`. */
  delayExit?: number
  /** CSS `transform-origin` used during the Grid enter/exit. Defaults to `'top center'`. */
  transformOriginGrid?: string
  /** CSS `transform-origin` used during the Table enter/exit. Defaults to `'top center'`. */
  transformOriginTable?: string
  /** Vertical translation offset (px) applied at the start of the enter animation. Defaults to `6`. */
  translateY?: number
  /** Horizontal scale factor applied at the start of the enter animation. Defaults to `0.992`. */
  horizontalScale?: number
  /** Delay after the new pane mounts before its enter animation begins. Defaults to `12`. */
  mountDelay?: number
  /** Extra wait time (ms) after the exit animation completes before swapping the DOM. Defaults to `30`. */
  hold?: number
}

/**
 * Default motion profile used when callers do not provide custom switch config.
 *
 * The values are tuned for a quick but readable transition between Grid and Table
 * while minimizing layout jump perception.
 */
export const DEFAULT_ANIMATION: Required<TAnimationConfig> = {
  enter: 220,
  exit: 140,
  ease: 'cubic-bezier(0.22, 0.61, 0.36, 1)',
  delayEnter: 12,
  delayExit: 0,
  transformOriginGrid: 'top center',
  transformOriginTable: 'top center',
  translateY: 6,
  horizontalScale: 0.992,
  mountDelay: 12,
  hold: 30
}

/**
 * Props for `CombinedTransition`, the low-level transition wrapper.
 */
interface ICombinedTransitionProps {
  /** Controls whether the child should be shown. */
  open: boolean
  /** Enter/exit durations in milliseconds. */
  timeout: { enter: number; exit: number }
  /** Delay before applying the enter animation after mount. */
  mountDelay: number
  /** Resolved animation config (fully required). */
  animationConfig: Required<TAnimationConfig>
  /** Optional style overrides (commonly transform origin). */
  style?: CSSProperties
  /** Single child element to receive animated inline style. */
  children: ReactElement
}

/**
 * Reusable transition wrapper combining fade + transform with controlled
 * mount/unmount timing to avoid abrupt content flashing.
 */
export const CombinedTransition: FC<ICombinedTransitionProps> = (props) => {
  const { open, timeout, mountDelay, animationConfig, style, children } = props
  const [mounted, setMounted] = useState(open)
  const [showing, setShowing] = useState(open)

  const mountTimerRef = useRef<number | null>(null)
  const unmountTimerRef = useRef<number | null>(null)

  useEffect(() => {
    if (open) {
      if (unmountTimerRef.current) {
        window.clearTimeout(unmountTimerRef.current)
        unmountTimerRef.current = null
      }
      setMounted(true)
      if (mountDelay > 0) {
        if (mountTimerRef.current) window.clearTimeout(mountTimerRef.current)
        mountTimerRef.current = window.setTimeout(() => {
          setShowing(true)
          mountTimerRef.current = null
        }, mountDelay)
      } else {
        setShowing(true)
      }
      return
    }

    setShowing(false)
    if (mountTimerRef.current) {
      window.clearTimeout(mountTimerRef.current)
      mountTimerRef.current = null
    }

    if (unmountTimerRef.current) {
      window.clearTimeout(unmountTimerRef.current)
    }
    unmountTimerRef.current = window.setTimeout(() => {
      setMounted(false)
      unmountTimerRef.current = null
    }, timeout.exit)
  }, [mountDelay, open, timeout.exit])

  useEffect(() => {
    return () => {
      if (mountTimerRef.current) {
        window.clearTimeout(mountTimerRef.current)
      }
      if (unmountTimerRef.current) {
        window.clearTimeout(unmountTimerRef.current)
      }
    }
  }, [])

  if (!mounted) return null

  const transitionStyle: CSSProperties = {
    transitionProperty: 'opacity, transform',
    transitionTimingFunction: animationConfig.ease,
    transitionDuration: `${showing ? timeout.enter : timeout.exit}ms`,
    transitionDelay: `${showing ? animationConfig.delayEnter : animationConfig.delayExit}ms`,
    transformOrigin: style?.transformOrigin || animationConfig.transformOriginGrid,
    willChange: 'opacity, transform',
    backfaceVisibility: 'hidden',
    opacity: showing ? 1 : 0,
    transform: showing
      ? 'translate3d(0, 0, 0) scaleX(1)'
      : `translate3d(0, ${animationConfig.translateY}px, 0) scaleX(${animationConfig.horizontalScale})`,
    ...style
  }

  return cloneElement(children, { style: transitionStyle })
}
